import { BillInvoice, ContractedPlace, EnergyDataPoint, UserAccount } from '../types/energy';
import { 
  DEFAULT_TARIFF, 
  generateDailyUsage, 
  generateHourlyUsage, 
  generateMonthlyUsage, 
  getPlaceData,
  INITIAL_ACCOUNT, 
  REAL_CONTRACTED_PLACES,
  DEMO_CONTRACTED_PLACES 
} from './mockData';
import { 
  getSqliteDatabase, 
  loadSqliteBundle, 
  saveSqliteBundle, 
  LocalSyncBundle 
} from './sqliteDatabase';

export type StoredEnergyBundle = LocalSyncBundle;

// Synchronous fast-boot in-memory cache
let inMemoryBundle: StoredEnergyBundle | null = null;

function getInitialFallbackBundle(): StoredEnergyBundle {
  let hasSavedCreds = false;
  let savedEmail = '';

  try {
    if (typeof localStorage !== 'undefined') {
      const rawCreds = localStorage.getItem('symenergy_portal_credentials_v3');
      const rawPw = localStorage.getItem('symenergy_portal_password_v3');
      if (rawCreds && rawPw) {
        const parsed = JSON.parse(rawCreds);
        if (parsed && parsed.email) {
          hasSavedCreds = true;
          savedEmail = parsed.email;
        }
      }
    }
  } catch {}

  let savedAddress = '';
  let savedSupplyPointId = '';
  try {
    if (typeof localStorage !== 'undefined') {
      const addr = localStorage.getItem('symenergy_saved_service_address');
      const spId = localStorage.getItem('symenergy_saved_supply_point_id');
      if (addr && !addr.includes('千代田区')) savedAddress = addr;
      if (spId && spId !== '0300000000000000000000') savedSupplyPointId = spId;
    }
  } catch {}

  return {
    account: {
      ...INITIAL_ACCOUNT,
      accountNumber: hasSavedCreds ? savedEmail : '',
      accountHolder: hasSavedCreds ? savedEmail : '',
      currentPlaceId: '',
      contractedPlaces: [],
      serviceAddress: savedAddress,
      paymentDetails: '',
      tariff: {
        ...DEFAULT_TARIFF,
        mprnNumber: savedSupplyPointId
      }
    },
    places: [],
    selectedPlaceId: '',
    invoices: [],
    hourlyUsage: [],
    dailyUsage: [],
    monthlyUsage: [],
    lastSyncAt: '',
    autoSyncEnabled: hasSavedCreds
  };
}

/**
 * Returns the current local bundle (synchronous cache, backed by SQLite WASM).
 * Guarantees zero-network offline readability!
 */
export function getLocalSyncedBundle(): StoredEnergyBundle {
  if (inMemoryBundle) {
    return inMemoryBundle;
  }
  inMemoryBundle = getInitialFallbackBundle();

  // Trigger background asynchronous hydration from SQLite WASM
  loadSqliteBundle().then((sqliteBundle) => {
    inMemoryBundle = sqliteBundle;
  }).catch((err) => {
    console.warn('[SQLite WASM] Initial load notice', err);
  });

  return inMemoryBundle;
}

/**
 * Asynchronously loads the bundle directly from SQLite WASM database.
 */
export async function getSqliteSyncedBundle(overridePlaceId?: string): Promise<StoredEnergyBundle> {
  const bundle = await loadSqliteBundle(overridePlaceId);
  inMemoryBundle = bundle;
  return bundle;
}

/**
 * Persists all scraped/synced portal data directly to SQLite WASM relational tables.
 * Resolves only once the snapshot has been written to the device; rejects if it could not,
 * so a Sync can report failure instead of claiming data was saved.
 */
export function saveLocalSyncedBundle(bundle: Partial<StoredEnergyBundle>): Promise<void> {
  if (inMemoryBundle) {
    inMemoryBundle = {
      ...inMemoryBundle,
      ...bundle
    };
  }

  if (bundle.selectedPlaceId) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('symenergy_selected_place_id', bundle.selectedPlaceId);
      }
    } catch {}
  }

  return saveSqliteBundle(bundle).catch((err) => {
    console.error('[SQLite WASM] Failed saving local energy bundle to SQLite', err);
    throw err;
  });
}

/**
 * Switches the selected contracted place and adapts the user account view accordingly,
 * then persists the choice to SQLite WASM. Awaiting it guarantees the selection survived
 * before the caller reports success or the page is reloaded.
 */
export async function selectContractedPlace(placeId: string, currentBundle: StoredEnergyBundle): Promise<StoredEnergyBundle> {
  if (!currentBundle.places || currentBundle.places.length === 0) {
    return currentBundle;
  }
  const targetPlace = currentBundle.places.find((p) => p.id === placeId) || currentBundle.places[0];
  if (!targetPlace) return currentBundle;

  const updatedAccount: UserAccount = {
    ...currentBundle.account,
    accountHolder: targetPlace.contractName || currentBundle.account.accountHolder,
    serviceAddress: targetPlace.address || currentBundle.account.serviceAddress,
    contractCapacity: targetPlace.contractCapacity || currentBundle.account.contractCapacity,
    currentPlaceId: targetPlace.id,
    tariff: {
      ...DEFAULT_TARIFF,
      mprnNumber: targetPlace.supplyPointId || currentBundle.account.tariff.mprnNumber,
      name: targetPlace.planName ? `シン・エナジー${targetPlace.planName}` : currentBundle.account.tariff.name
    }
  };

  const updatedBundle: StoredEnergyBundle = {
    ...currentBundle,
    selectedPlaceId: targetPlace.id,
    account: updatedAccount
  };

  inMemoryBundle = updatedBundle;
  await saveLocalSyncedBundle({
    selectedPlaceId: targetPlace.id,
    account: updatedAccount
  });

  // Telemetry rows are keyed by place_id, so switching must read back the newly selected
  // place's own series instead of reusing the previous place's arrays.
  return getSqliteSyncedBundle(targetPlace.id);
}

/**
 * Calculates milliseconds remaining until the upcoming 1:00 AM local time.
 */
export function getMillisecondsUntilNext1AM(): number {
  const now = new Date();
  const next1AM = new Date(now);
  next1AM.setHours(1, 0, 0, 0);

  if (now.getTime() >= next1AM.getTime()) {
    next1AM.setDate(next1AM.getDate() + 1);
  }

  return next1AM.getTime() - now.getTime();
}

/**
 * Formats the next 1:00 AM synchronization time for UI display.
 */
export function getNext1AMDisplayString(lang: 'ja' | 'en' = 'ja'): string {
  const now = new Date();
  const next1AM = new Date(now);
  next1AM.setHours(1, 0, 0, 0);
  if (now.getTime() >= next1AM.getTime()) {
    next1AM.setDate(next1AM.getDate() + 1);
  }

  const month = next1AM.getMonth() + 1;
  const day = next1AM.getDate();
  const isTomorrow = next1AM.getDate() !== now.getDate();

  if (lang === 'ja') {
    return `${isTomorrow ? '明日 ' : '本日 '}${month}月${day}日 01:00`;
  }
  return `${isTomorrow ? 'Tomorrow' : 'Today'} ${month}/${day} 01:00 AM`;
}

/**
 * Checks whether the app missed today's 1:00 AM sync (e.g. device was offline or closed).
 */
export function hasMissed1AMSyncToday(lastSyncIso: string): boolean {
  if (!lastSyncIso) return true;
  const lastSync = new Date(lastSyncIso);
  const now = new Date();

  const today1AM = new Date(now);
  today1AM.setHours(1, 0, 0, 0);

  if (now.getTime() >= today1AM.getTime()) {
    return lastSync.getTime() < today1AM.getTime();
  }

  return false;
}
